import { datumKort } from "@/lib/datum";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Onboarding: eenmalige intro (per rol) + "wat is er nieuw" changelog. Blokkeert nooit: laadt de
// gegevens los; faalt dat, dan gebeurt er niets en werkt de app gewoon door. De ingangen ('Intro' en
// 'Nieuw') zitten in de bestaande nav-balk (Layout) — geen extra strook, scheelt hoogte op mobiel.
export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    let ok = true;
    api.get("/onboarding").then((r) => {
      if (!ok) return;
      setData(r.data);
      if (!r.data.intro?.seen) { setStep(0); setIntroOpen(true); }
      if (r.data.changelog?.hasNew) setBannerOpen(true);
      window.dispatchEvent(new CustomEvent("onboarding:state", { detail: { hasNew: !!r.data.changelog?.hasNew } }));
    }).catch(() => {});
    return () => { ok = false; };
  }, []);

  useEffect(() => {
    const oi = () => { setStep(0); setIntroOpen(true); };
    const oc = () => openChangelog();
    window.addEventListener("onboarding:open-intro", oi);
    window.addEventListener("onboarding:open-changelog", oc);
    return () => {
      window.removeEventListener("onboarding:open-intro", oi);
      window.removeEventListener("onboarding:open-changelog", oc);
    };
  });

  const screens = data?.intro?.screens || [];
  const isBl = user?.role === "bedrijfsleider";

  const finishIntro = ({ goPrep = false } = {}) => {
    setIntroOpen(false);
    api.post("/onboarding/intro-seen").catch(() => {});
    setData((d) => (d ? { ...d, intro: { ...d.intro, seen: true } } : d));
    if (goPrep) navigate("/voorbereiding");
  };
  const markChangelogSeen = () => {
    setBannerOpen(false);
    api.post("/onboarding/changelog-seen").catch(() => {});
    setData((d) => (d ? { ...d, changelog: { ...d.changelog, hasNew: false } } : d));
    window.dispatchEvent(new CustomEvent("onboarding:state", { detail: { hasNew: false } }));
  };
  const openChangelog = () => { setChangelogOpen(true); if (bannerOpen) markChangelogSeen(); };

  const cl = data?.changelog?.entries || [];

  return (
    <>
      {/* v18-D1: geen balk meer; de rode stip op "Nieuw"/"Meer" opent de changelog. */}

      {/* Intro-dialog: mobiel-eerst, max 3 schermen, overslaan mag */}
      <Dialog open={introOpen} onOpenChange={(o) => { if (!o) finishIntro(); }}>
        <DialogContent data-testid="onboarding-intro-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">{screens[step]?.title || "Welkom"}</DialogTitle>
            <DialogDescription className="sr-only">Korte introductie bij OM-Cockpit</DialogDescription>
          </DialogHeader>
          <p data-testid="intro-body" className="text-sm text-zinc-500 leading-relaxed min-h-[80px]">{screens[step]?.body}</p>
          <div className="flex items-center justify-center gap-1.5 py-1">
            {screens.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-blue-600" : "w-1.5 bg-zinc-300"}`} />
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <button data-testid="intro-skip" onClick={() => finishIntro()} className="text-xs font-medium text-zinc-400 hover:text-zinc-700">
              Overslaan
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button data-testid="intro-prev" onClick={() => setStep((s) => s - 1)}
                  className="px-3 py-2 rounded-md text-sm font-semibold text-zinc-700 hover:bg-zinc-100">Vorige</button>
              )}
              {step < screens.length - 1 ? (
                <button data-testid="intro-next" onClick={() => setStep((s) => s + 1)}
                  className="px-4 py-2 rounded-md text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800">Volgende</button>
              ) : (
                <button data-testid="intro-done" onClick={() => finishIntro({ goPrep: isBl })}
                  className="px-4 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700">{isBl ? "Voorbereiding aanleveren" : "Aan de slag"}</button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Volledige changelog, altijd bereikbaar */}
      <Dialog open={changelogOpen} onOpenChange={setChangelogOpen}>
        <DialogContent data-testid="changelog-dialog" className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Wat is er nieuw</DialogTitle>
            <DialogDescription className="sr-only">Overzicht van recente wijzigingen, gegroepeerd per release</DialogDescription>
          </DialogHeader>
          {cl.length === 0 ? (
            <p className="text-sm text-zinc-500">Nog geen updates om te tonen.</p>
          ) : (
            <div className="space-y-5">
              {cl.map((rel) => (
                <div key={rel.version} data-testid={`changelog-release-${rel.version}`}>
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="font-heading font-bold text-zinc-900 text-sm">{rel.title}</span>
                    <span className="text-xs text-zinc-400 font-mono">{datumKort(rel.date) || rel.date}</span>
                  </div>
                  <ul className="list-disc pl-5 space-y-1">
                    {rel.items.map((it, i) => <li key={i} className="text-sm text-zinc-500">{it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
